import { ModelBase } from "@shoothill/core";
import { action, observable, computed } from "mobx";
import moment from "moment";

export class EnvironmentFormModel extends ModelBase<EnvironmentFormModel> {
    // #region Constants

    public static readonly DEFAULT_ENVIRONMENT_ID = null;
    public static readonly DEFAULT_PROJECT_ID = "";
    public static readonly DEFAULT_NON_CONFORMANCE_TYPE_ID = "";
    public static readonly DEFAULT_FORMDATE = moment.utc().toDate();

    // Concerning Environment
    public static readonly DEFAULT_SUPPLIER_ID = null;
    public static readonly DEFAULT_ISSUEDTO_USER_ID = null;
    public static readonly DEFAULT_LOCATION = "";
    public static readonly DEFAULT_DESCRIPTION = "";
    public static readonly DEFAULT_ENVIRONMENT_PHOTO_FILES = [];

    // Concerning rectification
    public static readonly DEFAULT_RECTIFICATION_REQUIRED = false;
    public static readonly DEFAULT_RECTIFICATION_DATE = null;
    public static readonly DEFAULT_RECTIFICATION_DETAILS = "";

    // Concerning open/closed state
    public static readonly DEFAULT_COMPLETION_ISOPEN = true;
    public static readonly DEFAULT_COMPLETION_DESCRIPTION = "";
    public static readonly DEFAULT_COMPLETION_PHOTO_FILES = [];

    // Concerning audit
    public static readonly DEFAULT_CREATED_DATE = null;
    public static readonly DEFAULT_COMPLETED_BY_USED_ID = "";

    public static readonly DEFAULT_ROWVERSION = null;

    // #endregion Constants

    // #region Related Properties

    @observable
    public suppliers = observable<SupplierDTO>([]);

    @observable
    public inductionUsers = observable<InductionUserDTO>([]);

    @observable
    public workerUsers = observable<InductionUserDTO>([]);

    @computed
    public get users() {
        const groupInuductionUsers = this.inductionUsers.map((u) => {
            return {
                groupType: "INDUCTION_USER",
                groupDisplayName: "Inducted",
                ...u,
            };
        });

        const groupWorkerUsers = this.workerUsers.map((u) => {
            return {
                groupType: "WORKER_USER",
                groupDisplayName: "Darwin staff",
                ...u,
            };
        });

        return [...groupInuductionUsers, ...groupWorkerUsers];
    }

    // #endregion Related Properties

    // #region Form Properties

    @observable
    public id: string | null = EnvironmentFormModel.DEFAULT_ENVIRONMENT_ID;

    @observable
    public projectId: string = EnvironmentFormModel.DEFAULT_PROJECT_ID;

    @observable
    public nonConformanceTypeId: string = EnvironmentFormModel.DEFAULT_NON_CONFORMANCE_TYPE_ID;

    @observable
    public formDate: Date | null = EnvironmentFormModel.DEFAULT_FORMDATE;

    // Concerning environment
    @observable
    public supplierId: string | null = EnvironmentFormModel.DEFAULT_SUPPLIER_ID;

    @observable
    public userId: string | null = EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID;

    public get inductionUserId() {
        const result = this.users.find((u) => u.id === this.userId);

        return result?.groupType === "INDUCTION_USER" ? result!.id : EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID;
    }

    public get workerUserId() {
        const result = this.users.find((u) => u.id === this.userId);

        return result?.groupType === "WORKER_USER" ? result!.id : EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID;
    }

    @observable
    public location: string = EnvironmentFormModel.DEFAULT_LOCATION;

    @observable
    public description: string = EnvironmentFormModel.DEFAULT_DESCRIPTION;

    @observable
    public environmentPhotos: EnvironmentPhotosFile[] = EnvironmentFormModel.DEFAULT_ENVIRONMENT_PHOTO_FILES;

    // Concerning rectification
    @observable
    public rectificationRequired: boolean = EnvironmentFormModel.DEFAULT_RECTIFICATION_REQUIRED;

    @observable
    public rectificationDate: string | null = EnvironmentFormModel.DEFAULT_RECTIFICATION_DATE;

    @observable
    public rectificationDetails: string = EnvironmentFormModel.DEFAULT_RECTIFICATION_DETAILS;

    // Concerning open/closed state
    @observable
    public isOpen: boolean = EnvironmentFormModel.DEFAULT_COMPLETION_ISOPEN;

    @observable
    public isOpenInitial: boolean = EnvironmentFormModel.DEFAULT_COMPLETION_ISOPEN;

    @observable
    public rectificationDescription: string = EnvironmentFormModel.DEFAULT_COMPLETION_DESCRIPTION;

    // Concerning audit
    @observable
    public createdDate: string | null = EnvironmentFormModel.DEFAULT_CREATED_DATE;

    @observable
    public completedByUserId: string | null = EnvironmentFormModel.DEFAULT_COMPLETED_BY_USED_ID;

    // APM Review
    // Different DTO type?
    @observable
    public environmentRectificationPhotos: EnvironmentRectificationPhotoFile[] = EnvironmentFormModel.DEFAULT_COMPLETION_PHOTO_FILES;

    @observable
    public rowVersion: string | null = EnvironmentFormModel.DEFAULT_ROWVERSION;

    // #endregion Form Properties

    // #Start Custome Validation

    @computed
    public get validateUserId(): string {
        return this.userId === EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID ? "Please Select User" : "";
    }

    @computed
    public get validateLocation(): string {
        return this.location === EnvironmentFormModel.DEFAULT_LOCATION ? "Please enter exact location of non-conformance" : "";
    }

    @computed
    public get validateDescription(): string {
        return this.description === EnvironmentFormModel.DEFAULT_DESCRIPTION ? "Please enter a description" : "";
    }

    @computed
    public get validateEnvironmentPhoto(): string {
        return this.environmentPhotos === EnvironmentFormModel.DEFAULT_ENVIRONMENT_PHOTO_FILES ? "Please Upload Photo" : "";
    }

    // #End Custome Validation

    @action
    public fromRelatedDto(dto: EnvironmentAndRelatedResponseDTO): void {}

    @action
    public reset = () => {
        this.id = EnvironmentFormModel.DEFAULT_ENVIRONMENT_ID;
        this.projectId = EnvironmentFormModel.DEFAULT_PROJECT_ID;
        this.nonConformanceTypeId = EnvironmentFormModel.DEFAULT_NON_CONFORMANCE_TYPE_ID;
        this.formDate = EnvironmentFormModel.DEFAULT_FORMDATE;

        // Concerning environment
        this.supplierId = EnvironmentFormModel.DEFAULT_SUPPLIER_ID;
        this.userId = EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID;
        this.location = EnvironmentFormModel.DEFAULT_LOCATION;
        this.description = EnvironmentFormModel.DEFAULT_DESCRIPTION;
        this.environmentPhotos = EnvironmentFormModel.DEFAULT_ENVIRONMENT_PHOTO_FILES;

        // Concerning rectification
        this.rectificationRequired = EnvironmentFormModel.DEFAULT_RECTIFICATION_REQUIRED;
        this.rectificationDate = EnvironmentFormModel.DEFAULT_RECTIFICATION_DATE;
        this.rectificationDetails = EnvironmentFormModel.DEFAULT_RECTIFICATION_DETAILS;

        // Concerning open/closed state
        this.isOpen = EnvironmentFormModel.DEFAULT_COMPLETION_ISOPEN;
        this.isOpenInitial = EnvironmentFormModel.DEFAULT_COMPLETION_ISOPEN;
        this.rectificationDescription = EnvironmentFormModel.DEFAULT_COMPLETION_DESCRIPTION;

        // Concerning open/closed state
        this.createdDate = EnvironmentFormModel.DEFAULT_CREATED_DATE;
        this.completedByUserId = EnvironmentFormModel.DEFAULT_COMPLETED_BY_USED_ID;
        this.rowVersion = EnvironmentFormModel.DEFAULT_ROWVERSION;
    };

    public fromDto(model: EnvironmentDTO): void {
        for (let key in model) {
            if (model.hasOwnProperty(key)) {
                if (this[key] instanceof Date) {
                    this[key] = new Date(model[key]);
                } else if (key === "workerUserId" || key === "inductionUserId" || key === "issuedToInductionUserId") {
                    if (model[key]) {
                        this.userId = model[key];
                    }
                } else {
                    this[key] = model[key];
                }
            }
        }
    }

    public toDto(): EnvironmentDTO {
        return {
            id: this.id,
            projectId: this.projectId,
            nonConformanceTypeId: this.nonConformanceTypeId,
            formDate: this.formDate,

            supplierId: this.supplierId,
            issuedToInductionUserId: this.inductionUserId,
            workerUserId: this.workerUserId,
            location: this.location,
            description: this.description,

            rectificationRequired: this.rectificationRequired,
            rectificationDate: this.rectificationDate,
            rectificationDetails: this.rectificationDetails,

            isOpen: this.isOpen,
            rectificationDescription: this.rectificationDescription,

            isDeleted: false,
            rowVersion: this.rowVersion,
        };
    }
}

export interface EnvironmentRelatedResultResponseDTO {
    suppliers: SupplierDTO[];
    issuedToInductionUsers: InductionUserDTO[];
    workerUsers: InductionUserDTO[];
    environmentProjectDetails: EnvironmentProjectDetailsDTO;
}

export interface SafetyRelatedByIdResponseDTO {
    suppliers: SupplierDTO[];
    issuedToInductionUsers: InductionUserDTO[];
    workerUsers: InductionUserDTO[];
    environmentProjectDetails: EnvironmentProjectDetailsDTO;
    environmentPhotos: EnvironmentPhotosFile;
}

export interface EnvironmentProjectDetailsDTO {
    projectReference: string;
    projectName: string;
}

export interface SupplierDTO {
    id: string;
    displayName: string;
}

export interface InductionUserDTO {
    id: string;
    displayName: string;
    supplierId: string | null;
}

export interface IDisplayFile {
    id: string;
    displayName: string;
}

export interface EnvironmentAndRelatedResponseDTO {
    environment: EnvironmentDTO;
    environmentPhotos: EnvironmentPhotosDTO[];
}

export interface EnvironmentPhotosFile {
    safetyId: string | null;
    fileName: string;
    photoURL: string;
    createdByUserId: string;
    isDeleted: boolean;
}

export interface EnvironmentRectificationPhotoFile {
    safetyId: string | null;
    fileName: string;
    photoURL: string;
    createdByUserId: string;
    isDeleted: boolean;
}

export interface EnvironmentDTO {
    id: string | null;
    projectId: string;
    nonConformanceTypeId: string;
    formDate: Date | null;

    supplierId: string | null;
    issuedToInductionUserId: string | null;
    workerUserId: string | null;
    location: string;
    description: string;

    rectificationRequired: boolean;
    rectificationDate: string | null;
    rectificationDetails: string;

    isOpen: boolean;
    rectificationDescription: string;

    isDeleted: boolean;
    rowVersion: string | null;
}

export interface EnvironmentPhotosDTO {
    safetyId: string | null;
    fileName: string;
    photoUrl: string;
    createdByUserId: number | null;
    isDeleted: boolean;
}

export interface UpsertEnvironmentAndRelatedRequestDTO {
    environment: EnvironmentDTO;
    environmentPhotos: EnvironmentPhotosDTO[];
    environmentRectificationPhotos: EnvironmentPhotosDTO[];
}
